import { Component, OnInit, AfterViewChecked, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WidgetCreditsService } from 'src/app/services/widget-credits.service';

@Component({
  selector: 'app-widget-credit-history',
  templateUrl: './widget-credit-history.component.html',
  styleUrls: ['./widget-credit-history.component.css']
})
export class WidgetCreditHistoryComponent implements OnInit, AfterViewChecked {

  private lastHeight = 0;

  personId = '';
  token = '';

  person: any = null;
  invoiceCredits: any[] = [];
  installments: any[] = [];
  payment_base: any[] = [];

  invoiceSelected: any = null;
  installMentSelected: any = null;

  view = 1; // 1=créditos, 2=cuotas, 3=pagos de cuota
  loading = false;
  loadingInstallments = false;
  loadingPayments = false;
  error = '';
  errorType: 'auth' | 'permission' | 'network' | '' = '';

  installmentNexPayment: any;

  constructor(
    private route: ActivatedRoute,
    private widgetCreditsService: WidgetCreditsService,
    private el: ElementRef
  ) {}

  ngAfterViewChecked(): void {
    const h = this.el.nativeElement.scrollHeight;
    if (h > 0 && h !== this.lastHeight) {
      this.lastHeight = h;
      window.parent.postMessage({ type: 'widget-resize', height: h }, '*');
    }
  }

  ngOnInit(): void {
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';

    this.personId = this.route.snapshot.paramMap.get('personId') || '';
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.personId || !this.token) {
      this.error = 'Parámetros de autenticación incompletos';
      return;
    }

    this.loadCredits();
  }

  async loadCredits(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const result = await this.widgetCreditsService.getCredits(this.personId, this.token);

      if (result.success) {
        this.person = result.data.person;
        this.invoiceCredits = result.data.res || [];
      } else {
        this.error = 'No se pudieron cargar los créditos';
      }
    } catch (err: any) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  async loadInstallments(invoice: any): Promise<void> {
    this.loadingInstallments = true;
    this.invoiceSelected = invoice;

    try {
      const result = await this.widgetCreditsService.getInstallments(this.personId, invoice.code, this.token);

      if (result.success) {
        this.installments = result.data.res || [];

        this.installmentNexPayment = null;
        this.installments.forEach((value: any) => {
          if (value?.installMent?.payment_status?.code !== 'PaymentComplete' && !this.installmentNexPayment) {
            this.installmentNexPayment = value?.installMent?.code;
          }

          value['totalpaymentReference'] = 0;
          value?.paymentReference?.forEach((ref: any) => {
            value['totalpaymentReference'] = this.toFixedApp(
              value['totalpaymentReference'] + (ref?.payment?.amount || 0)
            );
          });

          // Fees payment references
          value?.fees?.forEach((fee: any) => {
            if (Array.isArray(fee?.paymentsFeeReferences)) {
              fee['totalFeePaymentReference'] = fee.paymentsFeeReferences.reduce(
                (acc: number, curr: any) => acc + (curr?.payment?.amount || 0), 0
              );
            } else {
              fee['totalFeePaymentReference'] = 0;
            }
          });

          if (value.installMent) {
            value.installMent['total_dueBalance'] = this.toFixedApp(
              (value.installMent?.dueBalance || 0) - (value.totalpaymentReference || 0)
            );
          }
        });

        this.view = 2;
      }
    } catch (err: any) {
      console.error('Error loading installments:', err);
      this.error = 'Error al cargar las cuotas. Intenta de nuevo.';
      this.errorType = 'network';
    } finally {
      this.loadingInstallments = false;
    }
  }

  async loadInstallmentPayments(installment: any): Promise<void> {
    this.loadingPayments = true;
    this.installMentSelected = installment;

    try {
      const result = await this.widgetCreditsService.getInstallmentPayments(this.personId, installment.code, this.token);

      if (result.success) {
        this.payment_base = result.data?.res || [];
        this.view = 3;
      }
    } catch (err: any) {
      console.error('Error loading installment payments:', err);
    } finally {
      this.loadingPayments = false;
    }
  }

  closeInstallment(): void {
    if (this.view === 2) {
      this.installments = [];
      this.view = 1;
      this.invoiceSelected = null;
      this.installmentNexPayment = null;
    } else if (this.view === 3) {
      this.view = 2;
      this.installMentSelected = null;
      this.payment_base = [];
      this.installmentNexPayment = null;
    }
  }

  // --- Stats ---

  get pendingCount(): number {
    return this.invoiceCredits.filter((i: any) => i?.payment_status?.code === 'PaymentPending').length;
  }

  get completedCount(): number {
    return this.invoiceCredits.filter((i: any) => i?.payment_status?.code === 'PaymentComplete').length;
  }

  get trashCount(): number {
    return this.invoiceCredits.filter((i: any) => i?.payment_status?.code === 'PaymentTrash').length;
  }

  get totalCount(): number {
    return this.pendingCount + this.completedCount;
  }

  // --- Helpers ---

  getStatusDisplayName(statusName: string): string {
    if (!statusName) return '';
    const map: Record<string, string> = {
      'Pago Pendiente': 'Pendiente',
      'Pago Completado': 'Completado',
      'Pago Anulado': 'Anulado',
      'Pago Trash': 'Anulado'
    };
    return map[statusName] || statusName;
  }

  toFixedApp(x: any): number {
    const a: any = `e+${2}`;
    const b: any = `e-${2}`;
    return +(Math.round(x + a) + b);
  }

  restab(a: any, b: any): number {
    return this.toFixedApp(a - b);
  }

  private handleError(err: any): void {
    const status = err?.status || err?.error?.statusCode;
    const msg = err?.error?.msg || err?.error?.message || err?.message || '';

    if (status === 401 || msg.includes('Token') || msg.includes('jwt') || msg.includes('expired')) {
      this.errorType = 'auth';
      this.error = 'La sesión ha expirado. Regresa al CRM y vuelve a abrir esta vista.';
    } else if (status === 403 || msg.includes('Forbidden')) {
      this.errorType = 'permission';
      this.error = 'Acceso denegado.';
    } else if (status === 0 || !navigator.onLine) {
      this.errorType = 'network';
      this.error = 'Sin conexión con el servidor.';
    } else {
      this.errorType = 'network';
      this.error = 'No se pudo cargar la información. Intenta nuevamente.';
    }
  }
}
